import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useContext, useState, memo, useEffect } from 'react';
import { AuthContext } from '@/App';
import { useMinistry } from '@/contexts/MinistryContext';
import { LayoutDashboard, Users, CheckCircle, BarChart3, Settings, LogOut, UsersRound, UserCog, Calendar, Menu, X, ChevronDown } from 'lucide-react';

// NavItem component outside of Layout
const NavItem = memo(({ to, icon: Icon, children, end = false, onClick, color }) => (
  <NavLink 
    to={to} 
    end={end}
    onClick={onClick}
    className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}
    style={({ isActive }) => isActive ? { backgroundColor: color?.light, color: color?.dark } : {}}
  >
    <Icon size={20} />
    <span>{children}</span>
  </NavLink>
));

NavItem.displayName = 'NavItem';

const Layout = () => {
  const { user, logout, hasPermission } = useContext(AuthContext);
  const { ministries, currentMinistry, selectMinistry, getEntityName, colors } = useMinistry();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [ministryDropdownOpen, setMinistryDropdownOpen] = useState(false);

  // Aplicar cores CSS dinamicamente
  useEffect(() => {
    if (colors) {
      document.documentElement.style.setProperty('--ministry-primary', colors.primary);
      document.documentElement.style.setProperty('--ministry-light', colors.light);
      document.documentElement.style.setProperty('--ministry-dark', colors.dark);
    }
  }, [colors]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  const handleSelectMinistry = (ministry) => {
    selectMinistry(ministry);
    setMinistryDropdownOpen(false);
    window.location.reload();
  };

  return (
    <div className="flex min-h-screen">
      {/* Mobile Header - Com cor do ministério */}
      <div 
        className="lg:hidden fixed top-0 left-0 right-0 h-16 border-b z-40 flex items-center justify-between px-4"
        style={{ backgroundColor: colors.light, borderColor: colors.primary }}
      >
        <button 
          onClick={() => setSidebarOpen(true)}
          className="p-2 rounded-lg"
          style={{ color: colors.dark }}
        >
          <Menu size={24} />
        </button>
        <h1 
          className="text-lg font-bold"
          style={{ fontFamily: 'Space Grotesk', color: colors.dark }}
        >
          {currentMinistry?.name || 'Ministério Pulse'}
        </h1>
        <div className="w-10" />
      </div>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={closeSidebar}
        />
      )}

      {/* Sidebar */}
      <div className={`
        sidebar fixed h-full z-50
        w-64 lg:w-64
        transform transition-transform duration-300 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:translate-x-0
      `}>
        {/* Header colorido */}
        <div 
          className="p-4 lg:p-6 flex items-center justify-between"
          style={{ backgroundColor: colors.light }}
        >
          <div>
            <h1 
              className="text-xl lg:text-2xl font-bold"
              style={{ fontFamily: 'Space Grotesk', color: colors.dark }}
            >
              Ministério Pulse
            </h1>
            <p className="text-xs lg:text-sm mt-1" style={{ color: colors.dark, opacity: 0.8 }}>
              Gerenciamento de {getEntityName(true)}
            </p>
          </div>
          <button 
            onClick={closeSidebar}
            className="lg:hidden p-2 rounded-lg hover:bg-white/50"
            style={{ color: colors.dark }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Ministry Selector */}
        <div className="px-4 mb-4 mt-2">
          <div className="relative">
            <button
              data-testid="ministry-selector-btn"
              onClick={() => ministries.length > 0 && setMinistryDropdownOpen(!ministryDropdownOpen)}
              className="w-full flex items-center justify-between p-3 rounded-xl border-2 bg-white transition hover:shadow-md"
              style={{ borderColor: colors.primary }}
            >
              <div className="flex items-center gap-2">
                <div 
                  className="w-4 h-4 rounded-full"
                  style={{ backgroundColor: currentMinistry?.color || colors.primary }}
                />
                <span className="font-semibold text-gray-800 text-sm truncate">
                  {currentMinistry?.name || 'Adolescentes'}
                </span>
              </div>
              {ministries.length > 1 && (
                <ChevronDown 
                  size={18} 
                  className={`transition-transform ${ministryDropdownOpen ? 'rotate-180' : ''}`}
                  style={{ color: colors.primary }}
                />
              )}
            </button>

            {/* Dropdown */}
            {ministryDropdownOpen && ministries.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-50 overflow-hidden">
                <div className="p-2 border-b border-gray-100" style={{ backgroundColor: colors.light }}>
                  <p className="text-xs font-medium px-2" style={{ color: colors.dark }}>BASES DE DADOS</p>
                </div>
                {ministries.map((ministry) => (
                  <button
                    key={ministry.id}
                    onClick={() => handleSelectMinistry(ministry)}
                    className={`w-full flex items-center gap-3 p-3 hover:bg-gray-50 transition ${
                      currentMinistry?.id === ministry.id ? 'bg-gray-50' : ''
                    }`}
                  >
                    <div 
                      className="w-4 h-4 rounded-full"
                      style={{ backgroundColor: ministry.color || '#0ea5e9' }}
                    />
                    <div className="text-left flex-1">
                      <p className="font-medium text-gray-800 text-sm">{ministry.name}</p>
                      {ministry.description && (
                        <p className="text-xs text-gray-500 truncate">{ministry.description}</p>
                      )}
                    </div>
                    {currentMinistry?.id === ministry.id && (
                      <span 
                        className="text-xs px-2 py-0.5 rounded-full"
                        style={{ backgroundColor: colors.light, color: colors.dark }}
                      >
                        Ativo
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
        
        <nav className="mt-2 lg:mt-2 overflow-y-auto" style={{maxHeight: 'calc(100vh - 280px)'}}>
          {hasPermission('dashboard.view') && (
            <NavItem to="/" icon={LayoutDashboard} end onClick={closeSidebar} color={colors}>Dashboard</NavItem>
          )}
          
          {hasPermission('adolescents.view') && (
            <NavItem to="/adolescents" icon={Users} onClick={closeSidebar} color={colors}>{getEntityName(true)}</NavItem>
          )}
          
          {hasPermission('checkin.view') && (
            <NavItem to="/checkin" icon={CheckCircle} onClick={closeSidebar} color={colors}>Check-in</NavItem>
          )}
          
          {hasPermission('small_groups.view') && (
            <NavItem to="/small-groups" icon={UsersRound} onClick={closeSidebar} color={colors}>Pequenos Grupos</NavItem>
          )}
          
          {hasPermission('volunteers.view') && (
            <NavItem to="/volunteers" icon={UserCog} onClick={closeSidebar} color={colors}>Voluntariado</NavItem>
          )}
          
          {hasPermission('events.view') && (
            <NavItem to="/events" icon={Calendar} onClick={closeSidebar} color={colors}>Eventos</NavItem>
          )}
          
          {(hasPermission('reports.view') || hasPermission('reports.view_basic')) && (
            <NavItem to="/reports" icon={BarChart3} onClick={closeSidebar} color={colors}>Relatórios</NavItem>
          )}
          
          {hasPermission('settings.view') && (
            <NavItem to="/settings" icon={Settings} onClick={closeSidebar} color={colors}>Configurações</NavItem>
          )}
        </nav>
        
        {/* Footer com cor */}
        <div 
          className="absolute bottom-0 w-64 p-4 border-t bg-white"
          style={{ borderColor: colors.light }}
        >
          <NavLink 
            to="/profile" 
            onClick={closeSidebar}
            className={({ isActive }) => `flex items-center gap-3 p-2 rounded-lg mb-2 transition ${isActive ? 'text-white' : 'hover:bg-gray-50'}`}
            style={({ isActive }) => isActive ? { backgroundColor: colors.primary } : {}}
          >
            <div 
              className="w-8 h-8 lg:w-10 lg:h-10 rounded-full flex items-center justify-center text-white font-bold text-sm lg:text-base"
              style={{ background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.dark} 100%)` }}
            >
              {user?.name?.charAt(0).toUpperCase() || 'U'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-700 truncate">{user?.name}</p>
              <p className="text-xs text-gray-500 capitalize">{user?.role?.replace('_', ' ')}</p>
            </div>
          </NavLink>
          <button 
            onClick={handleLogout} 
            className="w-full flex items-center gap-2 p-2 text-gray-500 hover:bg-red-50 hover:text-red-600 rounded-lg transition"
          >
            <LogOut size={18} />
            <span className="text-sm">Sair</span>
          </button>
        </div>
      </div>
      
      {/* Main content */}
      <div className="flex-1 lg:ml-64">
        {/* Top bar com cor do ministério */}
        <div 
          className="hidden lg:block h-1"
          style={{ backgroundColor: colors.primary }}
        />
        <div className="p-4 lg:p-8 pt-20 lg:pt-8">
          <Outlet />
        </div>
      </div>
    </div>
  );
};

export default Layout;
